import { useFilterEntities } from "@features/ax/hooks";
import { TableModal } from "@features/ax/shared/tableModal";
import { getConditionLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { TooltipProvider } from "@shared/ui/tooltip";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface FilterAssetsProps {
  entityType: "system" | "network" | "facility";
  filterType: string;
  entityId: any;
  showCriticalAssetsModal: boolean;
  setShowCriticalAssetsModal: any;
  title?: string;
}

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-100 text-red-800";
  if (score >= 40) return "bg-yellow-100 text-yellow-800";
  return "bg-green-100 text-green-800";
};

export const FilterAssetsModal: React.FC<FilterAssetsProps> = ({
  entityId,
  entityType,
  filterType,
  showCriticalAssetsModal,
  setShowCriticalAssetsModal,
  title,
}) => {
  useEffect(() => {
    logEvent("alerts_tab_viewed");
  }, []);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const currency = useCurrency();
  const [page, setPage] = useState(1);

  const { data: criticalAssetsTableData } = useFilterEntities(
    entityId.toString(),
    entityType,
    filterType,
    page,
    PAGE_SIZE
  );
  const startItem = (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, criticalAssetsTableData?.count);

  const pagination = {
    currentPage: page,
    totalPages: Math.ceil(criticalAssetsTableData?.count / PAGE_SIZE),
    totalItems: criticalAssetsTableData?.count,
    startItem: startItem,
    endItem: endItem,
  };
  const criticalAssetsColumns = [
    { key: "assetNo", header: "Asset ID", type: "text" },
    { key: "assetName", header: "Asset Name", type: "text" },
    { key: "assetClassDisplay", header: "Asset Class", type: "text" },
    { key: "assetTypeDisplay", header: "Asset Type", type: "text" },

    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip: "The physical location or address of this asset.",
    },
    {
      key: "systemNetwork",
      header: "System/Network",
      type: "text",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {row.system ?? row.network ?? "NA"}
          </span>
        </div>
      ),
      headerTooltip:
        "The parent system or network this asset is a component of.",
    },
    {
      key: "replacementCost",
      header: "Replacement Cost",
      type: "custom",
      render: (value: number) => `${currency} ${value?.toLocaleString()}`,
      headerTooltip:
        "The estimated capital cost to replace this asset with a new one.",
    },
    {
      key: "conditionScores",
      header: "Condition Score",
      type: "custom",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The physical health of the asset, based on the most recent inspection. Higher is better.",
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getRiskColor(value)}>
            {row.riskStatus ?? "NA"}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
    },
    { key: "operationalStatus", header: "Operational Status", type: "text" },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive" as const;
      case "medium":
        return "secondary" as const;
      case "low":
        return "default" as const;
      default:
        return "outline" as const;
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div>
            {/* <PageHeader
              title="Active Alerts"
              subtitle={`Critical events, potential failures, and compliance issues requiring immediate attention for ${facility.facilityName}`}
            /> */}
          </div>
        </div>
      </div>
      <TableModal
        isOpen={showCriticalAssetsModal}
        title={title ? title : "Assets Out of Service"}
        onClose={() => setShowCriticalAssetsModal(false)}
        columns={criticalAssetsColumns}
        data={criticalAssetsTableData?.results ?? []}
        loading={false}
        searchPlaceholder="Search assets..."
        pagination={pagination}
        onPageChange={setPage}
      />
    </TooltipProvider>
  );
};
